/**
 * SCR-4 — Freshest-wins resolver shared by the Rerun panel's three
 * site-config surfaces (projectIntent, siteContext, typedPreferences).
 *
 * Background (SCR row 0.99291 — customer-trust data-loss hotfix):
 * The Rerun wizard's `useState` initialisers used to read the source
 * report's `rerunConfig.*` snapshot exclusively — they never consulted
 * the LIVE site state. When the user edited Project Intent (or any
 * other site-level config) on the Site Overview tab AFTER v1 was
 * generated, those edits were invisible to the v2 rerun. v2 silently
 * regenerated against the stale snapshot.
 *
 * This helper picks the freshest source on a per-field basis:
 *   - `live-site`        when the live site config was edited AFTER
 *                        the report was generated, OR when the live
 *                        value is present + non-empty and the only
 *                        alternative is the hardcoded default (see the
 *                        DATA-LOSS guard below), OR when the live value
 *                        is strictly RICHER than a present snapshot.
 *   - `report-snapshot`  when the snapshot exists and the live config
 *                        is older (or undated). Preserves
 *                        reproducibility for legacy reports.
 *   - `defaults`         when neither live nor snapshot is available
 *                        (legacy / blank). The caller supplies the
 *                        default; we do not invent one.
 *
 * Date parsing is guarded by `Number.isFinite()` — a malformed ISO
 * string short-circuits to the snapshot path (R2 from the SCR pack).
 *
 * DATA-LOSS guard (wf wq07loep2): the strict `liveMs > reportMs` gate
 * arbitrates LIVE-vs-SNAPSHOT only. It must NEVER cause a present,
 * non-empty live value to be discarded in favour of the hardcoded
 * `defaultValue`. Previously, a report generated AFTER the last intent
 * edit (the COMMON case) failed the strict gate and fell through to the
 * snapshot — and when no usable snapshot existed, all the way to the thin
 * default — silently dropping a full live intent. Now:
 *   - a strictly-newer SNAPSHOT may still win over live (reproducibility),
 *   - but a present, non-empty live value ALWAYS beats the default, and a
 *     RICHER live value beats a thinner snapshot regardless of the gate.
 * Callers wire `isNonEmpty` / `isRicher` to supply type-aware notions of
 * "present + non-empty" and "strictly richer"; both default to safe,
 * generic checks when omitted (generic `isNonEmpty` = defined + not an
 * empty string / empty object; generic `isRicher` = never, i.e. the
 * snapshot keeps its legacy precedence for plain string/object surfaces).
 */

export type ScrSource = 'live-site' | 'report-snapshot' | 'defaults';

export interface ResolveFreshestSiteConfigArgs<T> {
  /** Live value from the in-store SiteEntity (e.g. `site.projectIntent`). */
  liveValue: T | undefined;
  /** Snapshot captured on the report at generation time (e.g. `report.rerunConfig.projectIntent`). */
  snapshotValue: T | undefined;
  /** Hardcoded fallback — used when both live + snapshot are absent. */
  defaultValue: T;
  /** ISO 8601 timestamp of the last live-site write (`site.siteConfigUpdatedAt`). */
  liveUpdatedAt?: string;
  /** ISO 8601 timestamp of the report's createdAt — the "as of" anchor. */
  reportGeneratedAt?: string;
  /**
   * DATA-LOSS guard — type-aware "present + non-empty" predicate. When it
   * returns `true` for `liveValue`, that live value is NEVER discarded in
   * favour of `defaultValue`, regardless of the strict timestamp gate.
   * Defaults to a generic check (defined, not '' / not `{}`).
   */
  isNonEmpty?: (value: T) => boolean;
  /**
   * DATA-LOSS guard — type-aware "strictly richer than" predicate. When it
   * returns `true` for `(liveValue, candidate)`, the live value is
   * preferred over `candidate` (the snapshot or default) even when the
   * strict gate did not fire. Defaults to "never" so plain string/object
   * surfaces keep their legacy snapshot precedence; only callers that pass
   * a richness comparator (e.g. projectIntent) opt into rich-beats-thin.
   */
  isRicher?: (live: T, candidate: T) => boolean;
}

export interface ResolveFreshestSiteConfigResult<T> {
  value: T;
  source: ScrSource;
}

/**
 * Parse an ISO 8601 timestamp into a finite millisecond epoch. Returns
 * `undefined` on any unparseable / non-finite input — callers treat
 * that as "no signal" and short-circuit to the snapshot path.
 */
function parseFiniteMs(iso: string | undefined): number | undefined {
  if (!iso) return undefined;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : undefined;
}

/**
 * Generic "present + non-empty" check used when the caller does not supply
 * an `isNonEmpty` predicate. Treats `undefined`/`null`, the empty string,
 * and the empty object `{}` as empty; everything else is non-empty.
 */
function genericNonEmpty<T>(value: T | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
}

/**
 * Freshest-wins resolver. See file-header for the full SCR rationale and
 * the wf wq07loep2 DATA-LOSS guard.
 *
 * Resolution order:
 *   1. Strict freshness gate — `{ source: 'live-site' }` when ALL of:
 *        * `liveValue` is defined,
 *        * `liveUpdatedAt` parses to a finite epoch,
 *        * `reportGeneratedAt` parses to a finite epoch,
 *        * `liveUpdatedAt > reportGeneratedAt` (strict — equal timestamps
 *          fall through, so a same-millisecond apply-to-site write doesn't
 *          flip the surface mid-flight).
 *   2. RICHNESS guard — `{ source: 'live-site' }` when the live value is
 *      present + non-empty AND is strictly richer than the candidate it
 *      would otherwise lose to (snapshot if present, else default). A
 *      RICHER live value is never discarded for a thinner snapshot/default.
 *   3. Snapshot — `{ source: 'report-snapshot' }` when the snapshot exists.
 *   4. DEFAULT-PROTECTION guard — `{ source: 'live-site' }` when the live
 *      value is present + non-empty and the ONLY remaining alternative is
 *      the hardcoded default. A present live value ALWAYS beats the
 *      default, regardless of the strict gate.
 *   5. Default — `{ source: 'defaults' }`.
 */
export function resolveFreshestSiteConfig<T>(
  args: ResolveFreshestSiteConfigArgs<T>,
): ResolveFreshestSiteConfigResult<T> {
  const liveMs = parseFiniteMs(args.liveUpdatedAt);
  const reportMs = parseFiniteMs(args.reportGeneratedAt);

  const liveDefined = args.liveValue !== undefined;
  const liveNonEmpty =
    liveDefined && (args.isNonEmpty ? args.isNonEmpty(args.liveValue as T) : genericNonEmpty(args.liveValue));

  // 1. Strict freshness gate — live edited AFTER the report.
  if (
    liveDefined
    && liveMs !== undefined
    && reportMs !== undefined
    && liveMs > reportMs
  ) {
    return { value: args.liveValue as T, source: 'live-site' };
  }

  // 2. RICHNESS guard — a non-empty live value that is strictly richer than
  //    the candidate it would otherwise lose to is never thinned out. Only
  //    callers that supply `isRicher` (e.g. projectIntent) opt in here.
  if (liveNonEmpty && args.isRicher) {
    const candidate = args.snapshotValue !== undefined ? args.snapshotValue : args.defaultValue;
    if (args.isRicher(args.liveValue as T, candidate)) {
      return { value: args.liveValue as T, source: 'live-site' };
    }
  }

  // 3. Snapshot — reproducibility for legacy / strictly-newer-snapshot cases.
  if (args.snapshotValue !== undefined) {
    return { value: args.snapshotValue, source: 'report-snapshot' };
  }

  // 4. DEFAULT-PROTECTION guard — a present, non-empty live value ALWAYS
  //    beats the hardcoded default even when the strict gate did not fire.
  //    This is the core wf wq07loep2 fix: a full live intent is never
  //    discarded purely on the timestamp comparison.
  if (liveNonEmpty) {
    return { value: args.liveValue as T, source: 'live-site' };
  }

  // 5. Default.
  return { value: args.defaultValue, source: 'defaults' };
}
